import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Link,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddRounded as AddIcon,
  ArrowDownwardRounded as NextIcon,
  CheckCircleRounded as DoneIcon,
  CloseRounded as CloseIcon,
  HelpOutline as HelpOutlineIcon,
  OpenInNewRounded as OpenIcon,
  StorageOutlined as DataSourceIcon,
  TableChartOutlined as SheetIcon,
  TuneOutlined as ConfigureIcon,
} from '@mui/icons-material';
import useReportMapping from './useReportMapping';
import { ConfirmMappingDialog, FormatHelpDialog, MappingToast, MappingValuesDialog, SheetInlineEditor, SheetMappingDialog } from './MappingDialogs';
import { SearchFilter, segmentedSx } from '../../monthly/MultiSelectFilter';
import { cardSx, GREEN, GREEN_DARK, GREEN_SOFT, INK, LINE, MUTED } from '../../home/homeTheme';

const headSx = { fontSize: 12, fontWeight: 700, color: MUTED, bgcolor: 'var(--rt-subtle)', py: 1, borderBottom: `1px solid ${LINE}`, whiteSpace: 'nowrap' };
const cellSx = { fontSize: 13, color: INK, py: 0.9, borderBottom: `1px solid ${LINE}` };
const sectionTitleSx = { fontSize: 15, fontWeight: 700, color: INK };
const placeholderSx = { color: 'var(--rt-faint)', fontStyle: 'italic' };
const greenButtonSx = { textTransform: 'none', fontWeight: 700, boxShadow: 'none', bgcolor: GREEN };
const quietButtonSx = { textTransform: 'none', fontWeight: 600 };

// Stato di un foglio: Draft finché non viene finalizzato
function SheetStatus({ draft }) {
  return (
    <Box
      component="span"
      sx={{
        px: 0.9,
        py: 0.2,
        borderRadius: 999,
        fontSize: 11.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        bgcolor: draft ? 'var(--rt-cal-partial)' : GREEN_SOFT,
        color: draft ? 'var(--rt-amber)' : GREEN_DARK,
      }}
    >
      {draft ? 'Draft' : 'Complete'}
    </Box>
  );
}

function UnsavedChip({ label = 'Unsaved' }) {
  return (
    <Box component="span" sx={{ px: 1, py: 0.3, borderRadius: 999, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'var(--rt-cal-partial)', color: 'var(--rt-amber)' }}>
      {label}
    </Box>
  );
}

// Colori dello stato dei campi: configurato, incompleto, obbligatorio mancante, facoltativo mancante
const STATUS_COLORS = { configured: GREEN, invalid: '#E9A23B', requiredEmpty: 'var(--rt-danger)', optionalEmpty: 'var(--rt-faint)' };
const statusColor = (status, row) => (status.key === 'configured' ? STATUS_COLORS.configured : status.key === 'invalid' ? STATUS_COLORS.invalid : row.is_required ? STATUS_COLORS.requiredEmpty : STATUS_COLORS.optionalEmpty);
// Stato mostrato accanto al campo: se l'unico "dato" è la logica preimpostata (campo con una sola logica possibile),
// il campo non è stato toccato e resta "Not configured" invece di "Incomplete". Il salvataggio non cambia.
const displayStatus = (m, row) => {
  const status = m.getRowStatus(row);
  const untouched = !String(row.source_column_name || '').trim() && !String(row.single_value || '').trim() && row.allowed_logics.length === 1;
  return status.key === 'invalid' && untouched ? { key: 'empty', label: 'Not configured' } : status;
};
const Dot = ({ color, size = 9 }) => <Box sx={{ width: size, height: size, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />;

/**
 * Scheda Report Mapping di Projects V2. Flusso, campi, regole e messaggi sono quelli della pagina classica
 * (la logica è in useReportMapping): data source e master service, fogli Google (Field / SLA),
 * configurazione dei campi con bozza e finalizzazione, Mapping Values.
 * In più: il foglio in configurazione è nell'URL (?sheet=ID), così si può ricaricare o condividere il link.
 */
export default function ProjectReportMapping({ projectId, onDirtyChange }) {
  const m = useReportMapping(projectId);
  const [params, setParams] = useSearchParams();

  // Scelte del data source diverse da quelle salvate (il Jira Project ID conta solo se è stato modificato)
  const dataSourceDirty =
    !m.mappingLoading &&
    (m.selectedDataSource !== m.savedDataSourceType ||
      m.isMasterServiceDraft !== m.savedIsMasterService ||
      (m.selectedDataSource === 'jira' && m.isMasterServiceDraft && m.jiraProjectIdDirtyRef.current));
  const dirty = m.configHasUnsavedChanges || dataSourceDirty;
  useEffect(() => onDirtyChange(dirty), [dirty, onDirtyChange]);

  // ---- Foglio aperto nell'URL ----
  const initialSheet = useRef(params.get('sheet'));
  const urlReady = useRef(!initialSheet.current);
  // Si apre il foglio dell'URL solo dopo il primo caricamento dei dati
  const sawLoading = useRef(false);
  useEffect(() => {
    if (m.mappingLoading) {
      sawLoading.current = true;
      return;
    }
    if (urlReady.current || !sawLoading.current) return;
    const item = m.mappingItems.find((x) => String(x.id) === String(initialSheet.current));
    urlReady.current = true;
    if (item && m.savedIsMasterService && m.selectedDataSource === 'gsheet') m.handleOpenConfigDialog(item);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [m.mappingLoading, m.mappingItems, m.savedIsMasterService, m.selectedDataSource]);
  const selectedId = m.selectedSheetMapping ? String(m.selectedSheetMapping.id) : null;
  useEffect(() => {
    if (!urlReady.current || params.get('sheet') === selectedId) return;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedId) next.set('sheet', selectedId);
        else next.delete('sheet');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const structureLabel = (value) => m.dataStructureOptions.find((opt) => opt.value === value)?.label || value;
  const savedLabel = { jira: 'JIRA', gsheet: 'Google Sheets' }[m.savedDataSourceType] || null;
  const busy = m.mappingSaving || m.mappingLoading;
  const showSheets = m.selectedDataSource === 'gsheet' && m.savedIsMasterService;
  const sheet = m.selectedSheetMapping;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {m.mappingError && <Alert severity="error">{m.mappingError}</Alert>}

      {/* Data source */}
      <Box sx={{ ...cardSx, p: 2.25 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
          <DataSourceIcon sx={{ fontSize: 20, color: MUTED }} />
          <Typography sx={{ ...sectionTitleSx, flex: 1 }}>Data source</Typography>
          {m.mappingLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="success" />
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>Loading mapping configuration...</Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 12.5, color: MUTED }}>
              Saved: {savedLabel ? `${savedLabel}${m.savedIsMasterService ? ' · Master service' : ''}` : 'nothing yet'}
            </Typography>
          )}
        </Box>
        <Typography sx={{ fontSize: 13, color: MUTED, mb: 2 }}>
          Select a data source for this project. With Master service enabled, enter the Jira Project ID before saving, or save first to configure Google Sheets mappings.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControlLabel
            control={<Switch color="success" checked={m.isMasterServiceDraft} onChange={m.handleMasterServiceChange} disabled={busy} />}
            label={<Typography sx={{ fontSize: 14 }}>Master service</Typography>}
          />
          <TextField select size="small" label="Data Source" value={m.selectedDataSource} onChange={m.handleDataSourceChange} sx={{ minWidth: 260 }}>
            <MenuItem value="jira">JIRA</MenuItem>
            <MenuItem value="gsheet">Google Sheets</MenuItem>
          </TextField>
          <Box sx={{ flex: 1 }} />
          {dataSourceDirty && <UnsavedChip />}
          {/* Sempre cliccabile (come nella pagina classica), in evidenza solo con modifiche da salvare */}
          <Button
            variant={dataSourceDirty ? 'contained' : 'outlined'}
            color="success"
            onClick={m.handleSaveDataSource}
            disabled={busy}
            sx={{ ...greenButtonSx, minWidth: 150, ...(dataSourceDirty ? {} : { bgcolor: 'transparent', color: GREEN_DARK }) }}
          >
            {m.mappingSaving ? 'Saving...' : 'Save Data Source'}
          </Button>
        </Box>

        {m.selectedDataSource === 'jira' && m.isMasterServiceDraft && (
          <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${LINE}` }}>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK, mb: 1.25 }}>Jira Project ID</Typography>
            <TextField
              size="small"
              fullWidth
              label="jira_project_id"
              placeholder="e.g. RUNR or 10023"
              value={m.jiraProjectIdDraft}
              onChange={(e) => {
                m.setJiraProjectIdDraft(e.target.value);
                m.jiraProjectIdDirtyRef.current = true;
                m.mappingFormInitializedRef.current = true;
              }}
              onFocus={() => {
                m.isEditingJiraProjectIdRef.current = true;
              }}
              onBlur={() => {
                m.isEditingJiraProjectIdRef.current = false;
              }}
              sx={{ maxWidth: 420, '& input': { fontFamily: 'monospace' } }}
            />
          </Box>
        )}
      </Box>

      {/* Fogli Google */}
      {showSheets && (
        <Box sx={{ ...cardSx, p: 0, overflow: 'hidden' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', px: 2.25, py: 1.75 }}>
            <SheetIcon sx={{ fontSize: 20, color: MUTED }} />
            <Typography sx={{ ...sectionTitleSx, flex: 1 }}>Google Sheets Mapping</Typography>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={m.handleAddFieldMapping} sx={greenButtonSx}>
              Add Field Mapping
            </Button>
            <Button variant="contained" color="success" startIcon={<AddIcon />} onClick={m.handleAddSlaMapping} sx={greenButtonSx}>
              Add SLA Mapping
            </Button>
          </Box>
          <TableContainer>
            <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 870 }}>
              <colgroup>
                <col style={{ width: 190 }} />
                <col style={{ width: 220 }} />
                <col />
                <col style={{ width: 150 }} />
                <col style={{ width: 100 }} />
                <col style={{ width: 220 }} />
              </colgroup>
              <TableHead>
                <TableRow>
                  <TableCell sx={headSx}>Type</TableCell>
                  <TableCell sx={headSx}>File Name</TableCell>
                  <TableCell sx={headSx}>File URL</TableCell>
                  <TableCell sx={headSx}>Ticketing System</TableCell>
                  <TableCell sx={headSx}>Status</TableCell>
                  <TableCell sx={headSx}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {m.mappingItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ ...cellSx, textAlign: 'center', color: MUTED, py: 3 }}>
                      No mappings added yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  m.mappingItems.map((item) => {
                    const selected = sheet?.id === item.id;
                    const editing = m.mappingDialogOpen && m.editingSheetMappingId === item.id;
                    // Clic sulla riga: apre (o chiude) la modifica del foglio, subito sotto
                    const toggleEdit = () => (editing ? m.handleCloseMappingDialog() : m.handleEditSheetMapping(item));
                    const stop = (e) => e.stopPropagation();
                    return (
                      <React.Fragment key={item.id}>
                        <TableRow
                          hover
                          onClick={toggleEdit}
                          sx={{
                            cursor: 'pointer',
                            ...(selected || editing ? { bgcolor: GREEN_SOFT } : {}),
                            ...(editing ? { '& td': { borderBottom: 'none' } } : {}),
                          }}
                        >
                          <TableCell sx={cellSx}>
                            <Typography noWrap sx={{ fontSize: 13, fontWeight: 600 }}>
                              {item.mapping_type === 'field' ? 'Field Mapping' : `SLA Mapping${item.data_structure ? ` (${structureLabel(item.data_structure)})` : ''}`}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Typography noWrap sx={{ fontSize: 13 }} title={item.file_name}>
                              {item.file_name}
                            </Typography>
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Link
                              href={item.file_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              underline="hover"
                              title={item.file_url}
                              onClick={stop}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, fontSize: 13, color: 'var(--rt-forecast)' }}
                            >
                              <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.file_url}
                              </Box>
                              <OpenIcon sx={{ fontSize: 14, flexShrink: 0 }} />
                            </Link>
                          </TableCell>
                          <TableCell sx={{ ...cellSx, color: item.mapping_type === 'sla' ? MUTED : INK }}>
                            {item.mapping_type === 'sla' ? '-' : m.ticketingOptions.find((opt) => opt.value === item.ticketing_system)?.label || item.ticketing_system}
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <SheetStatus draft={item.is_draft !== false} />
                          </TableCell>
                          <TableCell sx={cellSx}>
                            <Box sx={{ display: 'flex', gap: 0.5, '& .MuiButton-root': { flexShrink: 0, whiteSpace: 'nowrap' } }}>
                              <Button
                                size="small"
                                variant={selected ? 'contained' : 'outlined'}
                                color="success"
                                startIcon={<ConfigureIcon />}
                                onClick={(e) => {
                                  stop(e);
                                  m.handleOpenConfigDialog(item);
                                }}
                                sx={{ ...quietButtonSx, boxShadow: 'none' }}
                              >
                                Configure
                              </Button>
                              <Button
                                size="small"
                                color="error"
                                onClick={(e) => {
                                  stop(e);
                                  m.handleDeleteSheetMapping(item);
                                }}
                                sx={quietButtonSx}
                              >
                                Delete
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                        {editing && (
                          <TableRow>
                            <TableCell colSpan={6} sx={{ p: 0, borderBottom: 'none' }}>
                              <SheetInlineEditor m={m} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {m.mappingItems.length > 0 && (
            <Box sx={{ px: 2.25, py: 1, borderTop: `1px solid ${LINE}`, bgcolor: 'var(--rt-subtle-2)' }}>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>Click a row to edit its file URL, name and ticketing system.</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Configurazione dei campi del foglio scelto */}
      {showSheets && sheet && <ConfigurePanel key={sheet.id} m={m} sheet={sheet} structureLabel={structureLabel} />}

      <SheetMappingDialog m={m} />
      <MappingValuesDialog m={m} />
      <FormatHelpDialog m={m} />
      <ConfirmMappingDialog m={m} />
      <MappingToast m={m} />
    </Box>
  );
}

function ConfigurePanel({ m, sheet, structureLabel }) {
  const editable = m.savedIsMasterService;
  const lookupOptions = (row) => m.lookupOptionsByDomain[m.getLookupDomainForRow(row)] || [];
  const finalized = sheet.is_draft === false;

  // ---- Avanzamento e conteggi (stessa regola di "Configured" usata dal salvataggio) ----
  const isDone = (row) => m.getRowStatus(row).key === 'configured';
  const rows = m.configRows;
  const required = rows.filter((row) => row.is_required);
  const requiredDone = required.filter(isDone).length;
  const optional = rows.filter((row) => !row.is_required);
  const optionalDone = optional.filter(isDone).length;
  const configuredCount = rows.filter(isDone).length;
  const counts = { all: rows.length, required: required.length, optional: optional.length, configured: configuredCount, not_configured: rows.length - configuredCount };
  const missingRequired = required.filter((row) => !isDone(row));

  // Dopo un "Save & Finalize" bloccato si evidenziano tutti gli obbligatori ancora incompleti
  const [finalizeTried, setFinalizeTried] = useState(false);
  const isHighlighted = (row) => finalizeTried && row.is_required && !isDone(row);

  // "Next missing": porta al prossimo obbligatorio incompleto (togliendo i filtri se lo nascondono)
  const rowRefs = useRef({});
  const lastJump = useRef(null);
  const [focusKey, setFocusKey] = useState(null);
  const nextMissing = () => {
    if (!missingRequired.length) return;
    const from = missingRequired.findIndex((row) => row.field_key === lastJump.current);
    const target = missingRequired[(from + 1) % missingRequired.length];
    lastJump.current = target.field_key;
    if (!m.filteredConfigRows.some((row) => row.field_key === target.field_key)) {
      m.setConfigSearchTerm('');
      m.setConfigFilter('all');
    }
    setFocusKey(target.field_key);
  };
  useEffect(() => {
    const el = focusKey && rowRefs.current[focusKey];
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.querySelector('input:not([type=hidden]):not([disabled])')?.focus({ preventScroll: true });
    setFocusKey(null);
  }, [focusKey, m.filteredConfigRows]);

  const progressValue = required.length ? (requiredDone / required.length) * 100 : 100;

  // Aperto il pannello (sotto l'elenco dei fogli), la pagina scorre fino a lui quando i campi sono caricati
  const panelRef = useRef(null);
  useEffect(() => {
    if (!m.configDialogLoading) panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [m.configDialogLoading]);

  return (
    <Box ref={panelRef} sx={{ ...cardSx, p: 0, overflow: 'visible', borderColor: GREEN, boxShadow: '0 0 0 3px rgba(90,176,49,0.10)', scrollMarginTop: 16 }}>
      {/* Intestazione */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', px: 2.25, pt: 1.75, pb: 1.25 }}>
        <ConfigureIcon sx={{ fontSize: 20, color: MUTED }} />
        <Typography sx={{ ...sectionTitleSx }}>
          Configure Mapping Fields —{' '}
          <Box component="span" sx={{ fontWeight: 600 }}>
            {sheet.file_name}
          </Box>
          {sheet.mapping_type === 'sla' && sheet.data_structure ? (
            <Box component="span" sx={{ fontWeight: 500, color: MUTED }}>
              {' '}
              (SLA · {structureLabel(sheet.data_structure)})
            </Box>
          ) : null}
        </Typography>
        <SheetStatus draft={!finalized} />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Close">
          <IconButton size="small" onClick={m.handleCloseConfigDialog}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Avanzamento */}
      {!m.configDialogLoading && rows.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', mx: 2.25, mb: 1.5, px: 1.75, py: 1.25, borderRadius: 2.5, bgcolor: 'var(--rt-subtle)', border: `1px solid ${LINE}` }}>
          <Box sx={{ minWidth: 220, flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.75, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }}>
                Required {requiredDone}/{required.length}
              </Typography>
              <Typography sx={{ fontSize: 12.5, color: MUTED }}>
                · Optional {optionalDone}/{optional.length}
              </Typography>
              {finalizeTried && missingRequired.length > 0 && (
                <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: 'var(--rt-danger)' }}>
                  · {missingRequired.length} required field{missingRequired.length === 1 ? '' : 's'} still missing
                </Typography>
              )}
            </Box>
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ height: 6, borderRadius: 3, bgcolor: 'var(--rt-track)', '& .MuiLinearProgress-bar': { borderRadius: 3, bgcolor: missingRequired.length ? STATUS_COLORS.invalid : GREEN } }}
            />
          </Box>
          {missingRequired.length > 0 ? (
            <Button size="small" variant="outlined" color="inherit" startIcon={<NextIcon />} onClick={nextMissing} sx={{ ...quietButtonSx, borderColor: 'var(--rt-pill-border)' }}>
              Next missing
            </Button>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: GREEN_DARK }}>
              <DoneIcon sx={{ fontSize: 18 }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'inherit' }}>All required fields are configured</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Filtri e legenda */}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', px: 2.25, pb: 1.25 }}>
        <SearchFilter value={m.configSearchTerm} onChange={m.setConfigSearchTerm} placeholder="Filter fields by label" width={240} />
        <ToggleButtonGroup exclusive size="small" value={m.configFilter} onChange={(e, v) => v && m.setConfigFilter(v)} sx={segmentedSx}>
          <ToggleButton value="all">All fields ({counts.all})</ToggleButton>
          <ToggleButton value="required">Required only ({counts.required})</ToggleButton>
          <ToggleButton value="optional">Optional only ({counts.optional})</ToggleButton>
          <ToggleButton value="configured">Configured only ({counts.configured})</ToggleButton>
          <ToggleButton value="not_configured">Not configured ({counts.not_configured})</ToggleButton>
        </ToggleButtonGroup>
        {m.configHasUnsavedChanges && <UnsavedChip label="Unsaved changes" />}
      </Box>
      <Box sx={{ display: 'flex', gap: 1.75, alignItems: 'center', flexWrap: 'wrap', px: 2.25, pb: 1.5 }}>
        <Typography sx={{ fontSize: 12, color: MUTED }}>Status:</Typography>
        {[
          [STATUS_COLORS.configured, 'Configured'],
          [STATUS_COLORS.invalid, 'Incomplete'],
          [STATUS_COLORS.requiredEmpty, 'Required not configured'],
          [STATUS_COLORS.optionalEmpty, 'Optional not configured'],
        ].map(([color, label]) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Dot color={color} size={8} />
            <Typography sx={{ fontSize: 12, color: MUTED }}>{label}</Typography>
          </Box>
        ))}
      </Box>

      {/* Campi */}
      {m.configDialogLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.25, py: 3, borderTop: `1px solid ${LINE}` }}>
          <CircularProgress size={18} color="success" />
          <Typography variant="body2">Loading field configuration...</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ maxHeight: '60vh', borderTop: `1px solid ${LINE}`, scrollbarGutter: 'stable' }}>
          <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headSx, minWidth: 220 }}>Field</TableCell>
                <TableCell sx={{ ...headSx, minWidth: 200 }}>Source Column Name</TableCell>
                <TableCell sx={{ ...headSx, width: 170, minWidth: 170 }}>Mapping Logic</TableCell>
                <TableCell sx={headSx}>Value Configuration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {m.filteredConfigRows.map((row) => {
                const status = displayStatus(m, row);
                const optionalEmpty = !row.is_required && !String(row.source_column_name || '').trim();
                const highlighted = isHighlighted(row);
                return (
                  <TableRow
                    key={row.field_key}
                    ref={(el) => {
                      rowRefs.current[row.field_key] = el;
                    }}
                    sx={{
                      bgcolor: highlighted ? 'rgba(229,57,53,0.07)' : optionalEmpty ? 'var(--rt-subtle)' : 'inherit',
                      ...(highlighted ? { '& td:first-of-type': { boxShadow: 'inset 3px 0 0 var(--rt-danger)' } } : {}),
                    }}
                  >
                    <TableCell sx={{ ...cellSx, minWidth: 220 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={status.label}>
                          <Box sx={{ display: 'flex' }}>
                            <Dot color={statusColor(status, row)} size={10} />
                          </Box>
                        </Tooltip>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: optionalEmpty ? MUTED : INK }}>
                          {row.label}
                          {row.is_required ? ' *' : ''}
                        </Typography>
                        {row.help_text && (
                          // Hover mostra l'help; il click lo fissa aperto (utile anche su touch)
                          <ClickAwayListener
                            onClickAway={() => {
                              if (m.pinnedHelpFieldKey === row.field_key) m.setPinnedHelpFieldKey(null);
                            }}
                          >
                            <Box component="span" sx={{ display: 'inline-flex' }}>
                              <Tooltip
                                title={row.help_text}
                                arrow
                                placement="right"
                                open={m.pinnedHelpFieldKey === row.field_key || m.hoveredHelpFieldKey === row.field_key}
                                onOpen={() => m.setHoveredHelpFieldKey(row.field_key)}
                                onClose={() => m.setHoveredHelpFieldKey(null)}
                              >
                                <IconButton
                                  size="small"
                                  aria-label={`Help for ${row.label}`}
                                  onClick={() => m.setPinnedHelpFieldKey((prev) => (prev === row.field_key ? null : row.field_key))}
                                  sx={{ p: 0.25, color: m.pinnedHelpFieldKey === row.field_key ? GREEN_DARK : MUTED, '&:hover': { color: GREEN_DARK } }}
                                >
                                  <HelpOutlineIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </ClickAwayListener>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <TextField
                        size="small"
                        value={row.source_column_name}
                        onChange={(e) => m.handleConfigRowChange(row.field_key, 'source_column_name', e.target.value)}
                        fullWidth
                        disabled={!editable}
                        sx={{ minWidth: 200, bgcolor: 'var(--rt-paper)' }}
                      />
                    </TableCell>
                    <TableCell sx={{ ...cellSx, width: 170, minWidth: 170 }}>
                      {row.allowed_logics.length === 1 ? (
                        <TextField
                          size="small"
                          fullWidth
                          value={m.mappingLogicOptions.find((opt) => opt.value === row.mapping_logic)?.label || row.mapping_logic}
                          InputProps={{ readOnly: true }}
                          disabled
                          sx={row.is_required ? { '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: INK, color: INK } } : undefined}
                        />
                      ) : (
                        <Select
                          size="small"
                          fullWidth
                          value={row.mapping_logic}
                          disabled={!editable}
                          onChange={(e) => {
                            const nextLogic = e.target.value;
                            m.handleConfigRowChange(row.field_key, 'mapping_logic', nextLogic);
                            if (nextLogic === 'choice' || nextLogic === 'dimension') {
                              m.ensureLookupOptions(m.getLookupDomainForRow({ ...row, mapping_logic: nextLogic }));
                            }
                          }}
                          displayEmpty
                          renderValue={(selected) => (selected ? m.mappingLogicOptions.find((opt) => opt.value === selected)?.label || selected : <span style={placeholderSx}>Select logic</span>)}
                          sx={{ minWidth: 150, bgcolor: 'var(--rt-paper)' }}
                        >
                          {m.mappingLogicOptions
                            .filter((opt) => row.allowed_logics.length === 0 || row.allowed_logics.includes(opt.value))
                            .map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell sx={cellSx}>
                      <ValueConfiguration m={m} row={row} editable={editable} options={lookupOptions(row)} />
                    </TableCell>
                  </TableRow>
                );
              })}
              {m.filteredConfigRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ ...cellSx, textAlign: 'center', color: MUTED, py: 3 }}>
                    No fields match the current filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Barra di salvataggio, sempre visibile in fondo */}
      <Box sx={{ position: 'sticky', bottom: 0, zIndex: 3, bgcolor: 'var(--rt-paper)', borderTop: `1px solid ${LINE}`, borderRadius: '0 0 16px 16px', px: 2.25, py: 1.25 }}>
        {m.configDialogError && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => m.setConfigDialogError(null)}>
            {m.configDialogError}
          </Alert>
        )}
        {/* Stesso ordine della pagina classica: Save Draft, Save & Finalize, Close */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="warning"
            onClick={() => m.handleSaveAllConfigRows({ finalize: false })}
            disabled={m.configDialogSavingAll || m.configDialogLoading || !m.configHasUnsavedChanges}
            sx={quietButtonSx}
          >
            {m.configDialogSavingAll ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setFinalizeTried(true);
              m.handleSaveAllConfigRows({ finalize: true });
            }}
            disabled={m.configDialogSavingAll || m.configDialogLoading || (!m.configHasUnsavedChanges && finalized)}
            sx={{ ...greenButtonSx, minWidth: 140 }}
          >
            {m.configDialogSavingAll ? 'Saving...' : finalized ? 'Save' : 'Save & Finalize'}
          </Button>
          <Button color="inherit" onClick={m.handleCloseConfigDialog} sx={{ textTransform: 'none' }}>
            Close
          </Button>
          <Typography sx={{ fontSize: 12.5, color: MUTED, ml: 1 }}>
            {finalized ? 'This mapping is complete.' : 'Save Draft keeps the fields filled in so far; Save & Finalize checks every required field.'}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

/** Colonna "Value Configuration": dipende dalla logica scelta, come nella pagina classica. */
function ValueConfiguration({ m, row, editable, options }) {
  const change = (value) => m.handleConfigRowChange(row.field_key, 'single_value', value);
  const placeholder = (logic) => <span style={placeholderSx}>{m.getSingleValuePlaceholder(logic)}</span>;

  if (row.mapping_logic === 'mapping') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="outlined" color="inherit" onClick={() => m.handleOpenMappingValuesDialog(row)} sx={{ ...quietButtonSx, borderColor: 'var(--rt-pill-border)' }}>
          {m.savedIsMasterService ? 'Mapping Values' : 'View Values'}
          {m.mappingValuesCountByField[row.field_key] ? ` · ${m.mappingValuesCountByField[row.field_key]}` : ''}
        </Button>
        {m.missingMappingAlertByField[row.field_key] && <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--rt-amber)' }}>Not configured</Typography>}
      </Box>
    );
  }
  if (row.mapping_logic === 'choice' && row.allow_multiple) {
    return (
      <Select
        size="small"
        fullWidth
        multiple
        value={row.single_value ? String(row.single_value).split(',').filter(Boolean) : []}
        displayEmpty
        disabled={!editable}
        onChange={(e) => {
          const next = Array.isArray(e.target.value) ? e.target.value : String(e.target.value).split(',');
          change(next.filter(Boolean).join(','));
        }}
        renderValue={(selected) =>
          !selected || selected.length === 0
            ? placeholder('choice')
            : selected
                .map((value) => {
                  const option = options.find((x) => String(x.value) === String(value));
                  return option ? m.formatLookupWithGroup(option) : value;
                })
                .join(', ')
        }
        sx={{ bgcolor: 'var(--rt-paper)' }}
      >
        {options.map((opt) => (
          <MenuItem key={`${row.field_key}-${opt.id ?? opt.value}`} value={opt.value}>
            {m.formatLookupWithGroup(opt)}
          </MenuItem>
        ))}
      </Select>
    );
  }
  if (row.mapping_logic === 'choice') {
    return (
      <Select
        size="small"
        fullWidth
        value={row.single_value || ''}
        displayEmpty
        disabled={!editable}
        onChange={(e) => change(e.target.value)}
        renderValue={(selected) => {
          if (!selected) return placeholder('choice');
          const option = options.find((x) => String(x.value) === String(selected));
          return option ? m.formatLookupWithGroup(option) : selected;
        }}
        sx={{ bgcolor: 'var(--rt-paper)' }}
      >
        {options.map((opt) => (
          <MenuItem key={`${row.field_key}-${opt.id ?? opt.value}`} value={opt.value}>
            {m.formatLookupWithGroup(opt)}
          </MenuItem>
        ))}
      </Select>
    );
  }
  if (row.mapping_logic === 'dimension') {
    return (
      <Select
        size="small"
        fullWidth
        value={row.single_value || ''}
        displayEmpty
        disabled={!editable}
        onChange={(e) => change(e.target.value)}
        renderValue={(selected) => {
          if (!selected) return placeholder('dimension');
          const option = options.find((x) => String(x.value) === String(selected));
          return option?.label || selected;
        }}
        sx={{ bgcolor: 'var(--rt-paper)' }}
      >
        {options.map((opt) => (
          <MenuItem key={`dimension-${opt.id ?? opt.value}`} value={opt.value}>
            {opt.label || opt.value}
          </MenuItem>
        ))}
      </Select>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {row.mapping_logic === 'format' && (
        <Tooltip title="Format Help">
          <IconButton size="small" onClick={() => m.setFormatHelpOpen(true)} sx={{ border: '1px solid var(--rt-pill-border)' }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <TextField
        size="small"
        value={row.single_value || ''}
        onChange={(e) => change(e.target.value)}
        fullWidth
        placeholder={m.getSingleValuePlaceholder(row.mapping_logic)}
        disabled={!editable || row.mapping_logic === 'mapping' || row.mapping_logic === 'move'}
        sx={{ bgcolor: 'var(--rt-paper)', ...(row.mapping_logic === 'format' ? { minWidth: 320 } : {}) }}
      />
    </Box>
  );
}
